import { useLazyQuery, useMutation } from '@apollo/client';
import {
    FindItineraryQuery,
    FindItineraryQueryVariables,
    KiuPassengerFormMutation,
    KiuPassengerFormMutationVariables,
    KiuReserveFlightsMutation,
    KiuReserveFlightsMutationVariables,
} from '@/graphql/types';
import { FIND_ITINERARY_QUERY } from '@/graphql/queries/flight-booking.gql';
import { useTranslation } from 'next-i18next';
import { KIU_PASSENGER_FORM, RESERVE_KIU_FLIGHTS } from '@/graphql/mutations/itinerary.gql';

export const useSearchFlightBooking = () => {
    const [findItineraryQuery, { loading, error }] = useLazyQuery<FindItineraryQuery>(FIND_ITINERARY_QUERY);

    const findItinerary = async ({
        variables,
        onCompleted,
        onError,
    }: {
        variables?: FindItineraryQueryVariables;
        onCompleted?: (data?: FindItineraryQuery) => Promise<void>;
        onError?: (error: unknown) => Promise<void>;
    }) => {
        try {
            const { data } = await findItineraryQuery({
                variables,
                fetchPolicy: 'network-only',
                onCompleted: (dat) => {
                    if (onCompleted) void onCompleted(dat);
                },
                onError: (err) => {
                    if (onError) void onError(err);
                },
            });

            return data?.findItinerary;
        } catch (err) {
            if (onError) void onError(err);
            throw err;
        }
    };

    return { findItinerary, loading, error };
};

export const useReserveKIUFlight = () => {
    const { i18n } = useTranslation(); // Translations
    const [kiuReserveFlightsMutation, { loading, error }] = useMutation<KiuReserveFlightsMutation, KiuReserveFlightsMutationVariables>(RESERVE_KIU_FLIGHTS, {
        context: { headers: { 'X-Language': i18n.language } },
    });

    const reservation = async ({
        variables,
        onCompleted,
        onError,
    }: {
        variables?: KiuReserveFlightsMutationVariables;
        onCompleted?: (data?: KiuReserveFlightsMutation) => Promise<void>;
        onUpdate?: (status?: number | null, errors?: ({ field: string; messages: string[] } | null)[] | null) => void;
        onError?: (error: unknown) => Promise<void>;
    }) => {
        try {
            const { data } = await kiuReserveFlightsMutation({
                variables,
                onCompleted: (dat) => {
                    if (onCompleted) void onCompleted(dat);
                },
                onError: (err) => {
                    if (onError) void onError(err);
                },
            });

            return data?.kiuReserveFlights;
        } catch (err) {
            if (onError) void onError(err);
            throw err;
        }
    };

    return { reservation, loading, error };
};

export const useKIUPassengerForm = () => {
    const { i18n } = useTranslation(); // Translations
    const [kiuPassengerFormMutation, { loading, error }] = useMutation<KiuPassengerFormMutation, KiuPassengerFormMutationVariables>(KIU_PASSENGER_FORM, {
        context: { headers: { 'X-Language': i18n.language } },
    });

    const passengerForm = async ({
        variables,
        onCompleted,
        onUpdate,
        onError,
    }: {
        variables?: any;
        onCompleted?: (data?: KiuPassengerFormMutation) => Promise<void>;
        onUpdate?: (status?: number | null, errors?: ({ field: string; messages: string[] } | null)[] | null) => void;
        onError?: (error: unknown) => Promise<void>;
    }) => {
        try {
            const { data } = await kiuPassengerFormMutation({
                variables,
                update: (cache, result) => {
                    const { status, errors } = result?.data?.kiuPassengerForm || {};
                    if (onUpdate) onUpdate(status, errors);
                },
                onCompleted: (dat) => {
                    if (onCompleted) void onCompleted(dat);
                },
                onError: (err) => {
                    if (onError) void onError(err);
                },
            });

            return data?.kiuPassengerForm;
        } catch (err) {
            if (onError) void onError(err);
            throw err;
        }
    };

    return { passengerForm, loading, error };
};
